// App-wide provider composition. Mounted from root.tsx so every route has
// Redux, TanStack Query, and i18n in context. Order matters — outer
// providers supply values inner providers may consume.
//
// Theming is CSS-driven via styles.css → theme.tailwind.css; no runtime
// ThemeProvider needed.

import { createPodiumQueryClient } from '@podium/platform/api'
import { QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import { Provider as ReduxProvider } from 'react-redux'

import { i18n } from './i18n'
import { store } from './store'

import type { ReactNode } from 'react'

// One QueryClient per session on the client; a fresh one per request on the
// server (TanStack Start hydration handles handoff). The factory bakes in
// the platform's retry / staleTime defaults — chiefly, no retry on 4xx, so
// stale-session 401s (and 403/404/422) don't fire four times before
// surfacing. Override via the `config` arg when an app needs different
// numbers.
const queryClient = createPodiumQueryClient()

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
      </QueryClientProvider>
    </ReduxProvider>
  )
}
